import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EMPTY_TASK_FORM,
  TaskForm,
  type TaskFormValue,
  taskFormToPayload,
  validateTaskForm,
} from "@/features/tasks/components/TaskForm";
import { useCreateTask } from "@/features/tasks/hooks/useTasks";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewTaskDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation("tasks");
  const navigate = useNavigate();
  const createTask = useCreateTask();
  const [value, setValue] = useState<TaskFormValue>(EMPTY_TASK_FORM);

  useEffect(() => {
    if (open) setValue(EMPTY_TASK_FORM);
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errorKey = validateTaskForm(value);
    if (errorKey) {
      toast.error(t(errorKey));
      return;
    }
    try {
      const created = await createTask.mutateAsync(taskFormToPayload(value));
      toast.success(t("toasts.created"));
      onOpenChange(false);
      navigate(`/tasks/${created.id}`);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("newTask.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <TaskForm value={value} onChange={setValue} />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              {t("actions.cancelDialog")}
            </Button>
            <Button type="submit" disabled={createTask.isPending}>
              {t("newTask.submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
